import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { ValidationUtilityService } from '../../shared/utils/validation.util/validation-utility.service';
import * as moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { CodeVerificationModalComponent } from '../mat-modal/code-verification-modal.component';
import { ProfileInfoBean, EmailInfoBean } from '../../../beans/account/account-module.bean';
import { SecurityInfoBean, AccountPreferencesBean } from '../../../beans/account/account-module.bean';
import { MASK_CONSTANTS } from '../../../constants/mask.constants';
import { AuthService } from '../../../services/core/auth.service';
import { Router, ActivatedRoute } from '../../../../../node_modules/@angular/router';
import { Config } from '../../../config/config';
import { METHOD_TYPE } from '../../../constants/service.constants';
import { ServiceRouter } from '../../../services/core/http-service-router.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { Environment } from '../../../config/environment';
import { JwtHelper } from '../../../services/core/jwt-helper.service';
import { ModalService } from '../../../services/core/modal.service';
import { AppUtil } from '../../../utils/app.util';
import { Subscription } from 'rxjs';
import { AnalyticsBean } from '../../../beans/analytics/analytics-module.bean';
import { EheAnalyticsDirective } from '../../shared/ehe-analytics/ehe-analytics.directive';
import { passwordSpecialCharsValidatorRegExp } from 'src/app/constants/app.constants';
import { ProfileService } from 'src/app/services/core/profile.service';
import { MfaService } from 'src/app/services/core/mfa.service';
import { MfaSetupState } from '../../shared/types';

/**
 * Profile component
 */
@Component({
	selector: 'app-profile',
	templateUrl: './profile.component.html',
	styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, OnDestroy {
	/** Personal info form group */
	updatePersonalInfoForm: FormGroup;
	/** Email info form group */
	updateEmailInfoForm: FormGroup<{
		primaryEmail: FormControl<any>;
		emails: FormArray<any>;
	}>;
	/** Security info form group */
	updateSecurityInfoForm: FormGroup;
	/** Preferences form group */
	updatePreferencesForm: FormGroup;
	/** Is Personal info editable flag */
	isPersonalInfoEditable: boolean = false;
	/** Is Email editable flag */
	isEmailEditable: boolean = false;
	/** Is Security info editable flag */
	isSecurityInfoEditable: boolean = false;
	/** Is Preferences editable flag */
	isPreferencesEditable: boolean = false;
	/** Data carrier subscriptions */
	subscriptions: Subscription = new Subscription();
	infoLoadPending = 3;
	/** States array */
	states = [];
	/** Max date for date picker */
	maxDate = new Date();
	/** Is profile loaded flag */
	isProfileLoaded: boolean = false;
	/** Is email loaded flag */
	isEmailLoaded: boolean = false;
	/** Profile info bean */
	profileInfoBean: ProfileInfoBean = new ProfileInfoBean();
	/** Additional emails array */
	additionalEmails: any = [];
	/** Email verified flag */
	emailVerified: boolean = false;
	/** Show resend verification flag */
	showResendVerificationFlag: boolean = false;
	/** Verification code */
	verificationCode: any = '';
	/** Primary email */
	primaryEmail: any = '';
	/** Can add new email flag */
	canAddNewEmail: boolean = true;
	/** Redirect condition enabled flag */
	redirectConditionEnabled: boolean;
	/** Updated email info bean */
	updatedEmailInfo: EmailInfoBean;
	querySubscription: any;
	/** Cellphone field viewchild */
	@ViewChild('cellphone', { static: false }) cellphoneField: ElementRef;
	/** Security info bean */
	securityInfoBean: SecurityInfoBean = new SecurityInfoBean();
	/** Account preferences bean */
	preferencesBean: AccountPreferencesBean = new AccountPreferencesBean();
	/** Zip display mask */
	public zipMask = MASK_CONSTANTS.ZIP;
	/** Phone number display mask */
	public phoneMask = MASK_CONSTANTS.PHONE;
	/** Analytics bean */
	analyticsData: AnalyticsBean;
	/** Sso flag */
	fromSso: boolean;
	/** Flag to hide password strength block on focus */
	hidePasswordStrengthBlock: boolean = true;
	isFromVaxPass = false;
	/** Custom Password Special Characters Validator RegExp */
	passwordSpecialCharsValidatorRegExp: RegExp = passwordSpecialCharsValidatorRegExp;
	hashFragment: string;
	userMfaStatus: boolean;

	/**
	 * Profile constructor
	 * @param dialog angular material dialog
	 * @param authService authentication service
	 * @param serviceRouter service router
	 * @param config config file that holds the service end points
	 * @param spinner spinner component
	 * @param environment environment file that supplies env values
	 * @param jwtService JWT token service
	 * @param modalService modal service
	 * @param router angular router
	 * @param activatedRoute contains the information about route associated
	 * @param eheAnalyticsDirective analytics directive
	 */
	constructor(
		private dialog: MatDialog,
		private authService: AuthService,
		private serviceRouter: ServiceRouter,
		private config: Config,
		private spinner: SpinnerComponent,
		private environment: Environment,
		private jwtService: JwtHelper,
		private modalService: ModalService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private eheAnalyticsDirective: EheAnalyticsDirective,
		private profileService: ProfileService,
		private mfaService: MfaService,
	) {
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'Account';
		this.analyticsData.pageUrl = '/account';
		this.fromSso = localStorage.getItem('sso') === 'true';
	}

	/**
	 * Component initialization
	 */
	ngOnInit() {
		// Gets profileInfo of member
		this.getProfileInfo();

		// Gets states - lookup data
		this.getStates();

		// Getting email info of member
		this.getEmailInfo();

		// Initializing preferences bean
		this.preferencesBean.emailNotification = true;
		this.preferencesBean.smsNotification = true;
		this.preferencesBean.receiveHealthNews = true;

		// Initializing Forms
		this.initializeUpdateSecurityInfoForm();
		// TODO Commenting as it is not required for this MVP
		// this.initializeUpdatePreferencesForm();

		this.subscriptions.add(
			this.activatedRoute.queryParams.subscribe(params => {
				if (params['q']) {
					try {
						let queryParams = atob(params['q']);
						queryParams = JSON.parse(queryParams);
						if (queryParams['from'] === 'appointment-review') {
							this.redirectConditionEnabled = true;
							this.updatePersonalInfo();
							// this.renderer.invokeElementMethod(this.cellphoneField.nativeElement, 'focus');
						}
					} catch (Exception) { }
				} else {
					this.redirectConditionEnabled = false;
				}
				if (params['action'] === 'edit-email') {
					this.updateEmail();
					this.isFromVaxPass = true;
				}
			})
		);
		this.subscriptions.add(
			this.activatedRoute.fragment.subscribe(hashFragment => {
				this.hashFragment = hashFragment;
			})
		);
		this.mfaService.fetchGlobalMfaStatus();
	}

	getGlobalMfaStatus() {
		return this.mfaService.ss_getGlobalMfaStatus();
	};

	getUserMfaStatus() {
		this.userMfaStatus = this.mfaService.ss_getUserMfaStatus();
		return this.userMfaStatus;
	};

	fetchUserMfaStatus() {
		this.infoLoadPending++;
		this.mfaService.fetchUserMfaActivatedStatus(this.profileInfoBean.email)
			.then(r => this.checkInfoLoadPending());
	}

	/**
	 * Gets basic profile information of member
	 */
	getProfileInfo() {
		this.spinner.startLoader();
		const epmsPID = this.jwtService.getEpmsId();

		this.subscriptions.add(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.profileApi.baseUrl + `/${epmsPID}/profile`,
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe({
					next: response => {
						this.isProfileLoaded = true;
						this.spinner.stopLoader();
						this.profileInfoBean = response;
						// Initializing empty for null values
						this.profileInfoBean.address = this.profileInfoBean.address !== 'null' ? this.profileInfoBean.address : '';
						this.profileInfoBean.address2 =
							this.profileInfoBean.address2 !== 'null' ? this.profileInfoBean.address2 : '';
						this.profileInfoBean.city = this.profileInfoBean.city !== 'null' ? this.profileInfoBean.city : '';
						this.profileInfoBean.state = this.profileInfoBean.state !== 'null' ? this.profileInfoBean.state : '';
						// Initializing Form with data received
						this.initializeUpdatePersonalInfoForm();
						this.fetchUserMfaStatus();
						this.checkInfoLoadPending();
					},
					error: (e) => {
						this.spinner.stopLoader();
						this.fetchUserMfaStatus();
						this.checkInfoLoadPending();
					}
				})
		);
	}

	/**
	 * Retrieves states list
	 */
	getStates() {
		this.subscriptions.add(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.schedulingApi.getStates,
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe({
					next: response => {
						this.states = response;
						this.checkInfoLoadPending();
					},
					error: () => {
						this.checkInfoLoadPending();
					}
				})
		);
	}

	/**
	 * Initializing personal info form
	 */
	initializeUpdatePersonalInfoForm() {
		const { firstName, lastName, dateOfBirth, gender, cellPhone, address, address2, zipCode, city, state, client } =
			this.profileInfoBean;
		let clientName = '';
		if (client !== null && client !== undefined) {
			const { name } = client;
			clientName = name;
		}
		this.updatePersonalInfoForm = new FormGroup({
			firstName: new FormControl(
				{ value: firstName, disabled: true },
				Validators.compose([
					Validators.required,
					Validators.minLength(2),
					Validators.maxLength(50),
					ValidationUtilityService.nameValidator,
				])
			),
			lastName: new FormControl(
				{ value: lastName, disabled: true },
				Validators.compose([
					Validators.required,
					Validators.minLength(2),
					Validators.maxLength(50),
					ValidationUtilityService.nameValidator,
				])
			),
			dateOfBirth: new FormControl(
				{ value: new Date(dateOfBirth), disabled: true },
				Validators.compose([Validators.required])
			),
			gender: new FormControl({ value: gender, disabled: true }, Validators.compose([Validators.required])),
			cellPhone: new FormControl(
				cellPhone,
				Validators.compose([Validators.required, ValidationUtilityService.phoneNumberValidator])
			),
			address: new FormControl(
				address,
				Validators.compose([Validators.required, ValidationUtilityService.addressValidator])
			),
			address2: new FormControl(address2),
			zipCode: new FormControl(
				zipCode,
				Validators.compose([Validators.required, ValidationUtilityService.zipCodeValidator])
			),
			city: new FormControl(city, Validators.compose([Validators.required, ValidationUtilityService.cityValidator])),
			state: new FormControl(state, Validators.compose([Validators.required])),
			employer: new FormControl({ value: clientName, disabled: true }, Validators.compose([Validators.required])),
		});

		// if (this.redirectConditionEnabled) {
		//   setTimeout(() => {
		//     // this.cellphoneField.nativeElement.classList.add('mat-focused');
		//     this.cellphoneField.nativeElement.focus();
		//   }, 1000);
		// }
	}

	/**
	 * Enables the updation of personal info by setting edit flag
	 */
	updatePersonalInfo() {
		this.analyticsData.id = 'Edit Personal Info';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Personal Info section';
		this.isPersonalInfoEditable = true;
	}

	/**
	 * Saves the personal info of member
	 */
	savePersonalInfo() {
		this.analyticsData.id = 'Save personal info';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Personal Info section';
		const client = this.profileInfoBean.client;
		this.profileInfoBean = {
			...this.profileInfoBean,
			...this.updatePersonalInfoForm.getRawValue(),
		};
		this.profileInfoBean.client = client;
		// Changing date format
		this.profileInfoBean.dateOfBirth = this.getDate(this.profileInfoBean.dateOfBirth);
		if (this.updatePersonalInfoForm.valid) {
			const epmsPID = this.jwtService.getEpmsId();
			this.subscriptions.add(
				this.serviceRouter
					.makeRequest(
						this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
						this.config.url.profileApi.baseUrl + `/${epmsPID}/profile`,
						METHOD_TYPE.SECURED_POST,
						this.profileInfoBean
					)
					.subscribe(
						(response: any) => {
							// this.getProfileInfo();
							this.isPersonalInfoEditable = false;
							this.initializeUpdatePersonalInfoForm();
							this.profileInfoBean.userMatched = JSON.parse(localStorage.getItem('isMatchedUser'));
							this.modalService.openInfoModal(
								this.config.content.errorTitle.ERRTIT001,
								this.config.content.errorType.ERRTYP001,
								this.config.content.profile.MSGPRO001,
								'OK'
							);
							this.analyticsData.id = this.config.content.profile.MSGPRO001;
							this.analyticsData.redirectedTo = '/account';
							this.analyticsData.placement = 'Save personal info modal';
							this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);

							if (this.redirectConditionEnabled) {
								this.modalService.getResult().subscribe(modalResponse => {
									this.router.navigate(['scheduling/create/appointment/v3']);
								});
							}
						},
						err => {
							this.modalService.openInfoModal(
								this.config.content.errorTitle,
								this.config.content.errorType.ERRTYP003,
								this.config.content.profile.MSGPRO002,
								'OK'
							);
							this.analyticsData.id = this.config.content.profile.MSGPRO002;
							this.analyticsData.redirectedTo = '/account';
							this.analyticsData.placement = 'Save personal info error modal';
							this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
						}
					)
			);
		}
	}

	/**
	 * Resets presonal info form
	 */
	cancelUpdatePersonalInfo() {
		this.analyticsData.id = 'Cancel update personal info';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Personal Info section';
		this.getProfileInfo();
		this.isPersonalInfoEditable = false;
	}

	/**
	 * Gets email information of member
	 * */
	getEmailInfo() {
		this.spinner.startLoader();
		this.additionalEmails = [];
		const epmsPID = this.jwtService.getEpmsId();
		this.subscriptions.add(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.profileEmailsApi.baseUrl + `/${epmsPID}/emails`,
					METHOD_TYPE.SECURED_GET,
					''
				)
				.subscribe({
					next: response => {
						// Getting primary and additional emails
						response.forEach(email => {
							if (email.primary) {
								this.primaryEmail = email;
							} else {
								this.additionalEmails.push(email);
							}
						});
						this.isEmailLoaded = true;
						this.initializeUpdateEmailForm();
						this.checkInfoLoadPending();
					},
					error: () => {
						this.checkInfoLoadPending();
					},
				})
		);
	}

	checkInfoLoadPending() {
		this.infoLoadPending--;
		if (this.infoLoadPending === 0) {
			if (this.hashFragment) {
				const mfaSettingsElem = document.getElementById(this.hashFragment);
				if (mfaSettingsElem) {
					setTimeout(() => {
						mfaSettingsElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}, 500);
				}
			}
			this.spinner.stopLoader();
		}
	}

	/**
	 * Initializing update email form
	 */
	initializeUpdateEmailForm() {
		this.updateEmailInfoForm = new FormGroup({
			primaryEmail: new FormControl(
				{ value: this.primaryEmail.email, disabled: this.fromSso },
				Validators.compose([Validators.required, ValidationUtilityService.emailValidator, Validators.maxLength(80)])
			),
			emails: new FormArray([]),
		});
		this.setEmails();
	}

	/**
	 * Adds new additional email to form group
	 * @param index email
	 */
	addNewAdditionalEmail(emailBean: any = '') {
		this.analyticsData.id = 'Add Email Address';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Email section';
		if (!emailBean) {
			this.additionalEmails.push(new EmailInfoBean());
		}
		const control = this.updateEmailInfoForm.controls.emails;
		control.push(
			new FormGroup({
				email: new FormControl(
					emailBean.email,
					Validators.compose([Validators.required, ValidationUtilityService.emailValidator])
				),
				id: new FormControl(emailBean.id),
			})
		);
		if (this.additionalEmails.length === 1) {
			this.canAddNewEmail = false;
		}
	}

	/**
	 * Removes additional email address
	 * @param index Index
	 */
	removeAdditionalEmail(index) {
		this.analyticsData.id = 'Remove email';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Email section';
		this.spinner.startLoader();
		const epmsPID = this.jwtService.getEpmsId();
		const control = this.updateEmailInfoForm.controls.emails;
		if (this.additionalEmails[index].email) {
			const emailBean = new EmailInfoBean();
			emailBean.email = AppUtil.getFormattedEmail(this.additionalEmails[index].email);
			emailBean.emailType = 'OTHER';
			emailBean.primary = false;
			this.subscriptions.add(
				this.serviceRouter
					.makeRequest(
						this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
						this.config.url.profileEmailsApi.baseUrl + `/${epmsPID}/emails`,
						METHOD_TYPE.SECURED_DELETE,
						emailBean
					)
					.subscribe(
						response => {
							this.spinner.stopLoader();
							this.getEmailInfo();
							this.modalService.openInfoModal(
								this.config.content.errorTitle.ERRTIT001,
								this.config.content.errorType.ERRTYP001,
								this.config.content.profile.MSGPRO003,
								'OK'
							);
							this.analyticsData.id = this.config.content.profile.MSGPRO003;
							this.analyticsData.redirectedTo = '/account';
							this.analyticsData.placement = 'Remove additional email modal';
							this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
							this.cancelUpdateEmail();
						},
						err => {
							this.spinner.stopLoader();
							this.modalService.openInfoModal(
								this.config.content.errorTitle.ERRTIT001,
								this.config.content.errorType.ERRTYP003,
								this.config.content.profile.MSGPRO004,
								'OK'
							);
							this.analyticsData.id = this.config.content.profile.MSGPRO004;
							this.analyticsData.redirectedTo = '/account';
							this.analyticsData.placement = 'Remove additional email error modal';
							this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
							this.getEmailInfo();
							this.initializeUpdateEmailForm();
						}
					)
			);
		}
		control.removeAt(index);
		this.additionalEmails.splice(index, 1);
	}

	/**
	 * Makes email as primary
	 * @param index
	 */
	makePrimary(index) {
		this.analyticsData.id = 'Make Preferred';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Email section';
		this.spinner.startLoader();
		const epmsPID = this.jwtService.getEpmsId();
		if (this.additionalEmails[index].email) {
			const emailBean = new EmailInfoBean();
			emailBean.emailType = 'OTHER';
			emailBean.email = AppUtil.getFormattedEmail(this.additionalEmails[index].email);
			this.subscriptions.add(
				this.serviceRouter
					.makeRequest(
						this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
						this.config.url.profileEmailsApi.baseUrl + `/${epmsPID}/emails/primary`,
						METHOD_TYPE.SECURED_POST,
						emailBean
					)
					.subscribe(
						response => {
							this.spinner.stopLoader();
							window.onpopstate = function (event) {
								location.href = '/';
							};
							this.getEmailInfo();
							this.modalService.openInfoModal(
								this.config.content.errorTitle.ERRTIT001,
								this.config.content.errorType.ERRTYP001,
								this.config.content.profile.MSGPRO013,
								'OK'
							);
							this.modalService.getResult().subscribe(res => {
								if (res.value === 'ok') {
									this.analyticsData.id = this.config.content.profile.MSGPRO013;
									this.analyticsData.redirectedTo = '/';
									this.analyticsData.placement = 'Make email primary modal';
									this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);

									this.authService.logoutUser();
									this.router.navigate(['/']);
								}
							});
							this.authService.logoutUser();
						},
						err => {
							this.spinner.stopLoader();
							this.getEmailInfo();
							this.initializeUpdateEmailForm();
						}
					)
			);
		}
	}

	/**
	 * Sets emails from bean to form control
	 */
	setEmails() {
		this.additionalEmails.forEach(element => {
			this.addNewAdditionalEmail(element);
		});
	}

	/**
	 * Enables the updation of email info by setting edit flag
	 */
	updateEmail() {
		this.analyticsData.id = 'Edit email';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Email section';
		this.isEmailEditable = true;
	}

	/**
	 * Updates primary email of the member
	 */
	updatePrimaryEmail() {
		window.onpopstate = function (event) {
			location.href = '/';
		};
		this.modalService.openInfoModal(
			this.config.content.errorTitle.ERRTIT001,
			this.config.content.errorType.ERRTYP001,
			this.config.content.profile.MSGPRO005,
			'OK'
		);
		this.analyticsData.id = this.config.content.profile.MSGPRO005;
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Update primary email modal';
		this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
		this.subscriptions.add(
			this.modalService.getResult().subscribe(res => {
				if (res.value === 'ok') {
					this.authService.logoutUser();
					// this.router.navigate(['/'], { queryParams: { tab: 'login'} });
					console.log(this.isFromVaxPass);
					if (this.isFromVaxPass) {
						this.router.navigate(['/'], {
							queryParams: { tab: 'login', from: 'vax-pass' },
						});
					} else {
						this.router.navigate(['/'], { queryParams: { tab: 'login' } });
					}
				}
			})
		);
		this.authService.logoutUser();
	}

	/**
	 * Adds new email
	 */
	addNewEmail() {
		const emailBean = new EmailInfoBean();
		let updatedEmail = '';
		if (this.updatedEmailInfo !== undefined && this.updatedEmailInfo !== null) {
			updatedEmail = this.updatedEmailInfo.email;
		}
		emailBean.email = AppUtil.getFormattedEmail(updatedEmail);
		emailBean.primary = false;
		emailBean.emailVerified = true;
		emailBean.uuid = this.profileInfoBean.uuid;
		this.subscriptions.add(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.profileEmailsApi,
					METHOD_TYPE.SECURED_POST,
					emailBean
				)
				.subscribe(
					response => {
						this.getEmailInfo();
					},
					err => {
						this.initializeUpdateEmailForm();
					}
				)
		);
	}

	/**
	 * Resets the update email form
	 */
	cancelUpdateEmail() {
		this.analyticsData.id = 'Cancel update email';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Email section';
		this.initializeUpdateEmailForm();
		this.isEmailEditable = false;
		if (this.isFromVaxPass) {
			this.router.navigate(['/vaxstatus'], {
				queryParams: { display: 'consent-form' },
			});
		}
	}

	/**
	 * On updating email address
	 * verifies the email by sending a verification code
	 * @param index
	 */
	verifyEmailAddress(index = -1) {
		this.analyticsData.id = 'Update email address';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Email section';
		this.spinner.startLoader();
		this.updatedEmailInfo = new EmailInfoBean();
		if (index !== -1) {
			this.updatedEmailInfo.email = AppUtil.getFormattedEmail(
				this.updateEmailInfoForm.getRawValue().emails[index].email
			);
			this.updatedEmailInfo.primary = false;
			this.updatedEmailInfo.isNew = this.updateEmailInfoForm.getRawValue().emails[index].isNew;
			this.updatedEmailInfo.id = this.updateEmailInfoForm.getRawValue().emails[index].id;
			this.sendVerificationCode(this.updatedEmailInfo);
		} else {
			this.updatedEmailInfo.email = AppUtil.getFormattedEmail(this.updateEmailInfoForm.getRawValue().primaryEmail);
			this.updatedEmailInfo.primary = true;
			this.updatedEmailInfo.id = this.primaryEmail.id;
			this.sendVerificationCode(this.updatedEmailInfo);
		}
	}

	/**
	 * Method to open verify email modal and process the result
	 * @param index
	 */
	sendVerificationCode(emailInfo) {
		const epmsPID = this.jwtService.getEpmsId();
		const request = new EmailInfoBean();
		request.email = AppUtil.getFormattedEmail(emailInfo.email);
		request.uuid = this.profileInfoBean.uuid;
		request.emailType = emailInfo.primary ? 'PRIMARY' : 'OTHER';
		const url = this.config.url.profileEmailsApi.baseUrl + `/${epmsPID}/emails`;
		this.spinner.startLoader();
		this.subscriptions.add(
			this.serviceRouter
				.makeRequest(this.environment.getEnvironment().EPMS_PROXY_BASE_URL, url, METHOD_TYPE.SECURED_PUT, request)
				.subscribe(
					response => {
						this.spinner.stopLoader();
						this.openVerifyEmailModal(request);
						this.spinner.stopLoader();
					},
					error => {
						console.error(`[${ProfileComponent.name}][${this.sendVerificationCode.name}]`, 'Error in send verification code', emailInfo, error);
						this.spinner.stopLoader();
					}
				)
		);
	}

	/**
	 * Opens code verification modal
	 * @param email
	 */
	openVerifyEmailModal(emailBean) {
		const dialogRef = this.dialog.open(CodeVerificationModalComponent, {
			width: '512px',
			data: emailBean,
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.getEmailInfo();
				this.modalService.openInfoModal(
					this.config.content.errorTitle.ERRTIT001,
					this.config.content.errorType.ERRTYP001,
					this.config.content.profile.MSGPRO007,
					'OK'
				);
				this.analyticsData.id = this.config.content.profile.MSGPRO007;
				this.analyticsData.redirectedTo = '/account';
				this.analyticsData.placement = 'Verify email modal';
				this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
				// Additional email is getting updated when verifying code hence not adding explicitly
				if (this.updatedEmailInfo.primary) {
					this.updatePrimaryEmail();
				} else {
					this.cancelUpdateEmail();
					this.modalService.openInfoModal(
						this.config.content.errorTitle.ERRTIT001,
						this.config.content.errorType.ERRTYP001,
						this.config.content.profile.MSGPRO008,
						'OK'
					);
					this.analyticsData.id = this.config.content.profile.MSGPRO008;
					this.analyticsData.redirectedTo = '/account';
					this.analyticsData.placement = 'Verify email error modal';
					this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
				}
			} else {
			}
		});
	}

	/**
	 * Initializing security info form
	 */
	initializeUpdateSecurityInfoForm() {
		this.updateSecurityInfoForm = new FormGroup(
			{
				oldPassword: new FormControl(
					'',
					Validators.compose([Validators.required, ValidationUtilityService.passwordValidator])
				),
				password: new FormControl(
					'',
					Validators.compose([Validators.required, ValidationUtilityService.passwordValidator])
				),
				confirmPassword: new FormControl(
					'',
					Validators.compose([Validators.required, ValidationUtilityService.passwordValidator])
				),
			},
			{ validators: ValidationUtilityService.matchingPasswords.bind(this) }
		);
	}

	/**
	 * Enables the updation of security info of member by setting edit flag
	 */
	updateSecurityInfo() {
		this.analyticsData.id = 'Edit security info';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Password section';
		this.isSecurityInfoEditable = true;
	}

	updateAccountStatus() {
		this.subscriptions.add(
			this.serviceRouter
				.makeRequest(
					this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
					this.config.url.registrationApi.createAccount + `/${this.jwtService.getEpmsId()}`,
					METHOD_TYPE.GENERAL_PUT,
					{
						uuid: this.profileInfoBean.uuid,
					}
				)
				.subscribe(
					respone => { },
					error => {
						console.error(`[${ProfileComponent.name}][${this.updateAccountStatus.name}]`, 'Error while updating user account status', error);
					}
				)
		);
	}
	/**
	 * Saves the security info of member
	 */
	saveSecurityInfo() {
		this.analyticsData.id = 'Save security info';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Password section';
		if (this.updateSecurityInfoForm.valid) {
			const passwords = this.updateSecurityInfoForm.getRawValue();
			this.spinner.startLoader();
			this.authService.updateUserPassword(passwords.oldPassword, passwords.password);
			this.subscriptions.add(
				this.authService.getResult().subscribe(
					res => {
						this.spinner.stopLoader();
						if (res.success) {
							this.updateAccountStatus();
							window.onpopstate = function (event) {
								location.href = '/';
							};
							this.modalService.openInfoModal(
								this.config.content.errorTitle.ERRTIT001,
								this.config.content.errorType.ERRTYP001,
								this.config.content.profile.MSGPRO010,
								'OK'
							);
							this.analyticsData.id = this.config.content.profile.MSGPRO010;
							this.analyticsData.redirectedTo = '/';
							this.analyticsData.placement = 'Update password modal';
							this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
							this.cancelUpdateSecurityInfo();
						}
					},
					err => {
						this.initializeUpdateSecurityInfoForm();
						this.spinner.stopLoader();
						console.error(`[${ProfileComponent.name}][${this.saveSecurityInfo.name}]`, err);
						this.modalService.openInfoModal(
							this.config.content.errorTitle.ERRTIT005,
							this.config.content.errorType.ERRTYP003,
							this.config.content.profile.MSGPRO012,
							'OK'
						);
						this.analyticsData.id = this.config.content.profile.MSGPRO012;
						this.analyticsData.redirectedTo = '/account';
						this.analyticsData.placement = 'Update password error modal';
						this.eheAnalyticsDirective.captureModalButtonData(this.analyticsData);
					}
				)
			);
		}
	}

	/**
	 * Resets the update security info form
	 */
	cancelUpdateSecurityInfo() {
		this.analyticsData.id = 'Cancel update security info';
		this.analyticsData.redirectedTo = '/account';
		this.analyticsData.placement = 'Password section';
		this.initializeUpdateSecurityInfoForm();
		this.isSecurityInfoEditable = false;
	}

	/**
	 * Initializing preferences form
	 */
	initializeUpdatePreferencesForm() {
		// TODO Commenting as it is not required for this MVP
		// this.updatePreferencesForm = new FormGroup({
		// 	emailNotification: new FormControl(this.preferencesBean.emailNotification),
		// 	smsNotification: new FormControl(this.preferencesBean.smsNotification),
		// 	receiveHealthNews: new FormControl(this.preferencesBean.receiveHealthNews)
		// });
		// this.updatePreferencesForm.get('emailNotification').disable();
		// this.updatePreferencesForm.get('smsNotification').disable();
		// this.updatePreferencesForm.get('receiveHealthNews').disable();
	}

	/**
	 * Enables the updating of preferences by setting edit flag
	 */
	updatePreferences() {
		// TODO Commenting as it is not required for this MVP
		// this.isPreferencesEditable = true;
		// this.enableUpdatePreferencesForm();
	}
	//
	/**
	 * Saves the updated preferences of member
	 */
	savePreferences() {
		// TODO Commenting as it is not required for this MVP
		// this.isPreferencesEditable = false;
	}
	//
	/**
	 * Resets the update preferences form
	 */
	cancelUpdatePreferences() {
		// TODO Commenting as it is not required for this MVP
		// this.updatePreferencesForm.get('emailNotification').setValue(this.preferencesBean.emailNotification);
		// this.updatePreferencesForm.get('smsNotification').setValue(this.preferencesBean.smsNotification);
		// this.updatePreferencesForm.get('receiveHealthNews').setValue(this.preferencesBean.receiveHealthNews);
		// this.disableUpdatePreferenceForm();
		// this.isPreferencesEditable = false;
	}

	/**
	 * Disables the preference form
	 */
	disableUpdatePreferenceForm() {
		// TODO Commenting as it is not required for this MVP
		// this.updatePreferencesForm.get('emailNotification').disable();
		// this.updatePreferencesForm.get('smsNotification').disable();
		// this.updatePreferencesForm.get('receiveHealthNews').disable();
	}

	/**
	 * Enables the preference form
	 */
	enableUpdatePreferencesForm() {
		// TODO Commenting as it is not required for this MVP
		// this.updatePreferencesForm.get('emailNotification').enable();
		// this.updatePreferencesForm.get('smsNotification').enable();
		// this.updatePreferencesForm.get('receiveHealthNews').enable();
	}

	/**
	 * Gets hash value of password
	 * @param string (password)
	 */
	getHash(string) {
		return new Array(string.length + 1).join('*');
	}

	/**
	 * Formats date in specified format
	 * @param date (input)
	 */
	getDate(date) {
		return moment(new Date(date)).format('MM/DD/YYYY');
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	updateMfaPreferences(event: Event) {
		this.spinner.startLoader();
		const checkbox = event.target as HTMLInputElement;
		const mfaEnabled = checkbox.checked; // Get the new state of the checkbox
		this.profileService.updateUserMfaPreference(`?enabled=${mfaEnabled}`).subscribe(
			response => {
				if (response.status == "OK") {
					this.spinner.stopLoader();
					this.modalService.showSuccessToaster('MFA Preference has been updated successfully!');
					this.userMfaStatus = mfaEnabled;
					this.mfaService.ss_setUserMfaStatus(mfaEnabled);
				}
			},
			error => {
				this.spinner.stopLoader();
				this.modalService.showErrorToaster();
				console.log('An error occure while updating MFA ', error);
			}
		);

	}

}
